import { createI18n } from 'vue-i18n'

// Common translations - loaded eagerly
import csCommon from './cs/common.json'
import skCommon from './sk/common.json'
import enCommon from './en/common.json'

import csValidation from './cs/validation.json'
import skValidation from './sk/validation.json'
import enValidation from './en/validation.json'

import csErrors from './cs/errors.json'
import skErrors from './sk/errors.json'
import enErrors from './en/errors.json'

import csBuilder from './cs/builder.json'
import skBuilder from './sk/builder.json'
import enBuilder from './en/builder.json'

import csAuth from './cs/auth.json'
import skAuth from './sk/auth.json'
import enAuth from './en/auth.json'

// Module translations - eagerly loaded for consistency
import csCategories from './cs/categories.json'
import skCategories from './sk/categories.json'
import enCategories from './en/categories.json'

import csCustomers from './cs/customers.json'
import skCustomers from './sk/customers.json'
import enCustomers from './en/customers.json'

import csOrders from './cs/orders.json'
import skOrders from './sk/orders.json'
import enOrders from './en/orders.json'

import csProducts from './cs/products.json'
import skProducts from './sk/products.json'
import enProducts from './en/products.json'

import csPages from './cs/pages.json'
import skPages from './sk/pages.json'
import enPages from './en/pages.json'

import csEmailTemplates from './cs/email-templates.json'
import skEmailTemplates from './sk/email-templates.json'
import enEmailTemplates from './en/email-templates.json'

import csUsers from './cs/users.json'
import skUsers from './sk/users.json'
import enUsers from './en/users.json'

import csProjects from './cs/projects.json'
import skProjects from './sk/projects.json'
import enProjects from './en/projects.json'

import csMenus from './cs/menus.json'
import skMenus from './sk/menus.json'
import enMenus from './en/menus.json'

import csContacts from './cs/contacts.json'
import skContacts from './sk/contacts.json'
import enContacts from './en/contacts.json'

import csContactGroups from './cs/contact-groups.json'
import skContactGroups from './sk/contact-groups.json'
import enContactGroups from './en/contact-groups.json'

import csForms from './cs/forms.json'
import skForms from './sk/forms.json'
import enForms from './en/forms.json'

import csVideos from './cs/videos.json'
import skVideos from './sk/videos.json'
import enVideos from './en/videos.json'

import csAssets from './cs/assets.json'
import skAssets from './sk/assets.json'
import enAssets from './en/assets.json'

import csDashboard from './cs/dashboard.json'
import skDashboard from './sk/dashboard.json'
import enDashboard from './en/dashboard.json'

import csNotifications from './cs/notifications.json'
import skNotifications from './sk/notifications.json'
import enNotifications from './en/notifications.json'

import csInvoicing from './cs/invoicing.json'
import skInvoicing from './sk/invoicing.json'
import enInvoicing from './en/invoicing.json'

import csEventReservations from './cs/event-reservations.json'
import skEventReservations from './sk/event-reservations.json'
import enEventReservations from './en/event-reservations.json'

import csSubscriptions from './cs/subscriptions.json'
import skSubscriptions from './sk/subscriptions.json'
import enSubscriptions from './en/subscriptions.json'

import csTickets from './cs/tickets.json'
import skTickets from './sk/tickets.json'
import enTickets from './en/tickets.json'

import csOrchestration from './cs/orchestration.json'
import skOrchestration from './sk/orchestration.json'
import enOrchestration from './en/orchestration.json'

import csLodging from './cs/lodging.json'
import skLodging from './sk/lodging.json'
import enLodging from './en/lodging.json'

import csAuditLog from './cs/audit-log.json'
import skAuditLog from './sk/audit-log.json'
import enAuditLog from './en/audit-log.json'

import csAurum from './cs/aurum.json'
import skAurum from './sk/aurum.json'
import enAurum from './en/aurum.json'

export type SupportedLocale = 'cs' | 'sk' | 'en'

export const SUPPORTED_LOCALES: SupportedLocale[] = ['cs', 'sk', 'en']

export const LOCALE_NAMES: Record<SupportedLocale, string> = {
  cs: 'Čeština',
  sk: 'Slovenčina',
  en: 'English'
}

function getDefaultLocale(): SupportedLocale {
  // Check localStorage first
  const stored = localStorage.getItem('locale') as SupportedLocale
  if (stored && SUPPORTED_LOCALES.includes(stored)) {
    return stored
  }

  // Check browser language
  const browserLang = navigator.language.slice(0, 2)
  if (browserLang === 'cs') return 'cs'
  if (browserLang === 'sk') return 'sk'

  // Fallback to English
  return 'en'
}

export const i18n = createI18n({
  legacy: false,
  locale: getDefaultLocale(),
  fallbackLocale: 'en',
  messages: {
    cs: {
      common: csCommon,
      validation: csValidation,
      errors: csErrors,
      builder: csBuilder,
      auth: csAuth,
      categories: csCategories,
      customers: csCustomers,
      orders: csOrders,
      products: csProducts,
      pages: csPages,
      emailTemplates: csEmailTemplates,
      users: csUsers,
      projects: csProjects,
      menus: csMenus,
      contacts: csContacts,
      contactGroups: csContactGroups,
      forms: csForms,
      videos: csVideos,
      assets: csAssets,
      dashboard: csDashboard,
      notifications: csNotifications,
      invoicing: csInvoicing,
      eventReservations: csEventReservations,
      subscriptions: csSubscriptions,
      tickets: csTickets,
      orchestration: csOrchestration,
      lodging: csLodging,
      auditLog: csAuditLog,
      aurum: csAurum
    },
    sk: {
      common: skCommon,
      validation: skValidation,
      errors: skErrors,
      builder: skBuilder,
      auth: skAuth,
      categories: skCategories,
      customers: skCustomers,
      orders: skOrders,
      products: skProducts,
      pages: skPages,
      emailTemplates: skEmailTemplates,
      users: skUsers,
      projects: skProjects,
      menus: skMenus,
      contacts: skContacts,
      contactGroups: skContactGroups,
      forms: skForms,
      videos: skVideos,
      assets: skAssets,
      dashboard: skDashboard,
      notifications: skNotifications,
      invoicing: skInvoicing,
      eventReservations: skEventReservations,
      subscriptions: skSubscriptions,
      tickets: skTickets,
      orchestration: skOrchestration,
      lodging: skLodging,
      auditLog: skAuditLog,
      aurum: skAurum
    },
    en: {
      common: enCommon,
      validation: enValidation,
      errors: enErrors,
      builder: enBuilder,
      auth: enAuth,
      categories: enCategories,
      customers: enCustomers,
      orders: enOrders,
      products: enProducts,
      pages: enPages,
      emailTemplates: enEmailTemplates,
      users: enUsers,
      projects: enProjects,
      menus: enMenus,
      contacts: enContacts,
      contactGroups: enContactGroups,
      forms: enForms,
      videos: enVideos,
      assets: enAssets,
      dashboard: enDashboard,
      notifications: enNotifications,
      invoicing: enInvoicing,
      eventReservations: enEventReservations,
      subscriptions: enSubscriptions,
      tickets: enTickets,
      orchestration: enOrchestration,
      lodging: enLodging,
      auditLog: enAuditLog,
      aurum: enAurum
    }
  }
})

// Set locale and persist
export function setLocale(locale: SupportedLocale): void {
  i18n.global.locale.value = locale
  localStorage.setItem('locale', locale)
  document.documentElement.lang = locale
}

export default i18n
